/**
 * Supabase utilities for interacting with the database
 */
import { supabase } from '../supabaseClient';

/**
 * Record a delivery tracking scan
 * @param {Object} scanData - The scan data to record
 * @param {string} scanData.tracking_number - The tracking number scanned
 * @param {string} scanData.status - The status of the scan ('success' or 'error')
 * @param {string} scanData.odoo_shipment_id - The Odoo shipment ID (if found)
 * @param {string} scanData.error_message - Error message (if any)
 * @returns {Promise<Object>} - The created record
 */
export async function recordDeliveryScan(scanData) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('qrlabel_delivery_scans')
      .insert({
        ...scanData,
        user_id: userId,
        scanned_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error recording delivery scan:', error);
    throw error;
  }
}

/**
 * Get delivery scan batches for the current user
 * @param {number} limit - Maximum number of batches to return
 * @returns {Promise<Array>} - Array of scan batches
 */
export async function getDeliveryScanBatches(limit = 10) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('qrlabel_delivery_batches')
      .select(`
        id,
        batch_name,
        created_at,
        user_id,
        total_scans,
        successful_scans
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting delivery scan batches:', error);
    throw error;
  }
}

/**
 * Create a new delivery scan batch
 * @param {string} batchName - Name of the batch
 * @returns {Promise<Object>} - The created batch
 */
export async function createDeliveryScanBatch(batchName) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('qrlabel_delivery_batches')
      .insert({
        batch_name: batchName,
        user_id: userId,
        total_scans: 0,
        successful_scans: 0
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating delivery scan batch:', error);
    throw error;
  }
}

/**
 * Record a scan in a batch and update batch counters
 * @param {Object} scanData - The scan data
 * @param {number} batchId - ID of the batch
 * @returns {Promise<Object>} - The updated batch data
 */
export async function recordBatchScan(scanData, batchId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // First record the scan
    const { data: scanRecord, error: scanError } = await supabase
      .from('qrlabel_delivery_scans')
      .insert({
        ...scanData,
        batch_id: batchId,
        user_id: userId,
        scanned_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (scanError) throw scanError;
    
    // First get the current counter values
    const { data: currentBatch, error: fetchError } = await supabase
      .from('qrlabel_delivery_batches')
      .select('total_scans, successful_scans')
      .eq('id', batchId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // Then manually update the counters with new values
    const { data: batchData, error: batchError } = await supabase
      .from('qrlabel_delivery_batches')
      .update({
        total_scans: (currentBatch?.total_scans || 0) + 1,
        successful_scans: scanData.status === 'success' 
          ? (currentBatch?.successful_scans || 0) + 1 
          : (currentBatch?.successful_scans || 0)
      })
      .eq('id', batchId)
      .eq('user_id', userId)
      .select()
      .single();
    
    if (batchError) throw batchError;
    return { scan: scanRecord, batch: batchData };
  } catch (error) {
    console.error('Error recording batch scan:', error);
    throw error;
  }
}

/**
 * Get scans for a specific batch
 * @param {number} batchId - ID of the batch
 * @returns {Promise<Array>} - Array of scans in the batch
 */
export async function getBatchScans(batchId) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('qrlabel_delivery_scans')
      .select(`
        id,
        tracking_number,
        status,
        odoo_shipment_id,
        error_message,
        scanned_at,
        images
      `)
      .eq('batch_id', batchId)
      .order('scanned_at', { ascending: false });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting batch scans:', error);
    throw error;
  }
}

/**
 * Upload an image to Supabase Storage
 * @param {File|Blob} file - The image file to upload
 * @param {string} bucket - The storage bucket name
 * @param {string} path - The path within the bucket
 * @returns {Promise<string>} - The public URL of the uploaded image
 */
export async function uploadImage(file, bucket, path) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Generate unique filename
    const fileExt = file.name ? file.name.split('.').pop() : 'jpg';
    const fileName = `${userId}/${path}/${Date.now()}.${fileExt}`;
    
    // Upload file
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || 'image/jpeg'
      });
    
    if (error) throw error;
    
    // For private buckets, return the storage path instead of public URL
    // We'll generate signed URLs when needed for display
    return data.path;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Delete an image from Supabase Storage
 * @param {string} imageUrl - The public URL of the image
 * @param {string} bucket - The storage bucket name
 * @returns {Promise<void>}
 */
export async function deleteImage(imageUrl, bucket) {
  try {
    // Extract path from URL
    const url = new URL(imageUrl);
    const pathMatch = url.pathname.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)/);
    
    if (!pathMatch || !pathMatch[1]) {
      throw new Error('Invalid image URL');
    }
    
    const filePath = pathMatch[1];
    
    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);
    
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
}

/**
 * Upload multiple images
 * @param {Array<File|Blob>} files - Array of image files
 * @param {string} bucket - The storage bucket name
 * @param {string} path - The path within the bucket
 * @returns {Promise<Array<string>>} - Array of public URLs
 */
export async function uploadMultipleImages(files, bucket, path) {
  try {
    const uploadPromises = files.map(file => uploadImage(file, bucket, path));
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
}

/**
 * Save logistics record with images
 * @param {Object} recordData - The logistics form data
 * @param {Array<File|Blob>} images - Array of image files
 * @returns {Promise<Object>} - The created record
 */
export async function saveLogisticsRecord(recordData, images = []) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Upload images first
    let imageUrls = [];
    if (images.length > 0) {
      imageUrls = await uploadMultipleImages(images, 'qrlabel-images', 'logistics');
    }
    
    // Save record
    const { data, error } = await supabase
      .from('qrlabel_logistics_records')
      .insert({
        user_id: userId,
        sku: recordData.sku,
        serial_numbers: recordData.serialNumbers || [],
        images: imageUrls,
        metadata: recordData.metadata || {}
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving logistics record:', error);
    throw error;
  }
}

/**
 * Save RMA record with images
 * @param {Object} recordData - The RMA form data
 * @param {Array<File|Blob>} images - Array of image files
 * @returns {Promise<Object>} - The created record
 */
export async function saveRMARecord(recordData, images = []) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Upload images first
    let imageUrls = [];
    if (images.length > 0) {
      imageUrls = await uploadMultipleImages(images, 'qrlabel-images', 'rma');
    }
    
    // Save record
    const { data, error } = await supabase
      .from('qrlabel_rma_records')
      .insert({
        user_id: userId,
        rma_number: recordData.rmaNumber,
        pallet_number: recordData.palletNumber,
        packaging_type: recordData.packagingType,
        condition: recordData.condition,
        dimensions: {
          length: recordData.length,
          width: recordData.width,
          height: recordData.height
        },
        weight: recordData.weight,
        notes: recordData.notes,
        images: imageUrls,
        metadata: recordData.metadata || {}
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving RMA record:', error);
    throw error;
  }
}

/**
 * Get logistics records for current user with signed URLs for images
 * @param {number} limit - Maximum number of records
 * @returns {Promise<Array>} - Array of logistics records
 */
export async function getLogisticsRecords(limit = 50) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('qrlabel_logistics_records')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // Generate signed URLs for images
    const recordsWithSignedUrls = await Promise.all(
      data.map(async (record) => {
        if (record.images && record.images.length > 0) {
          try {
            const signedImageUrls = await Promise.all(
              record.images.map(async (imagePath) => {
                try {
                  return await getSignedUrl(imagePath);
                } catch (error) {
                  console.error('Error generating signed URL for:', imagePath, error);
                  return null; // Return null for failed URLs
                }
              })
            );
            // Filter out null URLs
            return { ...record, images: signedImageUrls.filter(url => url !== null) };
          } catch (error) {
            console.error('Error processing images for record:', record.id, error);
            return { ...record, images: [] };
          }
        }
        return record;
      })
    );
    
    return recordsWithSignedUrls;
  } catch (error) {
    console.error('Error getting logistics records:', error);
    throw error;
  }
}

/**
 * Generate signed URL for private storage
 * @param {string} path - The storage path
 * @param {string} bucket - The bucket name
 * @param {number} expiresIn - Expiration time in seconds (default 1 hour)
 * @returns {Promise<string>} - Signed URL
 */
export async function getSignedUrl(path, bucket = 'qrlabel-images', expiresIn = 3600) {
  try {
    // If path is already a full URL (from old records), return it as is
    if (path.startsWith('http')) {
      return path;
    }
    
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);
    
    if (error) throw error;
    return data.signedUrl;
  } catch (error) {
    console.error('Error generating signed URL for path:', path, error);
    throw error;
  }
}

/**
 * Get RMA records for current user with signed URLs for images
 * @param {number} limit - Maximum number of records
 * @returns {Promise<Array>} - Array of RMA records
 */
export async function getRMARecords(limit = 50) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('qrlabel_rma_records')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // Generate signed URLs for images
    const recordsWithSignedUrls = await Promise.all(
      data.map(async (record) => {
        if (record.images && record.images.length > 0) {
          try {
            const signedImageUrls = await Promise.all(
              record.images.map(async (imagePath) => {
                try {
                  return await getSignedUrl(imagePath);
                } catch (error) {
                  console.error('Error generating signed URL for:', imagePath, error);
                  return null; // Return null for failed URLs
                }
              })
            );
            // Filter out null URLs
            return { ...record, images: signedImageUrls.filter(url => url !== null) };
          } catch (error) {
            console.error('Error processing images for record:', record.id, error);
            return { ...record, images: [] };
          }
        }
        return record;
      })
    );
    
    return recordsWithSignedUrls;
  } catch (error) {
    console.error('Error getting RMA records:', error);
    throw error;
  }
}